import API from '@sparefoot/react-core/symbols/api';
import { PaidLandingTypes, PPC_TYPE_STORAGE } from '../../config/constants';

// ------------------------------------
// Constants
// ------------------------------------
export const UNSUBSCRIBE_REQUEST = 'SSDC/EMAILS/UNSUBSCRIBE_REQUEST';
export const UNSUBSCRIBE_SUCCESS = 'SSDC/EMAILS/UNSUBSCRIBE_SUCCESS';
export const UNSUBSCRIBE_FAILURE = 'SSDC/EMAILS/UNSUBSCRIBE_FAILURE';

export const HOMEPAGE_REQUEST = 'SSDC/PAGES/HOMEPAGE_REQUEST';
export const HOMEPAGE_SUCCESS = 'SSDC/PAGES/HOMEPAGE_SUCCESS';
export const HOMEPAGE_FAILURE = 'SSDC/PAGES/HOMEPAGE_FAILURE';

export const DEFAULT_PAGE_REQUEST = 'SSDC/PAGES/DEFAULT_PAGE_REQUEST';
export const DEFAULT_PAGE_SUCCESS = 'SSDC/PAGES/DEFAULT_PAGE_SUCCESS';
export const DEFAULT_PAGE_FAILURE = 'SSDC/PAGES/DEFAULT_PAGE_FAILURE';

export const PAID_LANDING_TEMPLATE_REQUEST =
	'SFDC/PAGES/PAID_LANDING_TEMPLATE_REQUEST';
export const PAID_LANDING_TEMPLATE_SUCCESS =
	'SFDC/PAGES/PAID_LANDING_TEMPLATE_SUCCESS';
export const PAID_LANDING_TEMPLATE_FAILURE =
	'SFDC/PAGES/PAID_LANDING_TEMPLATE_FAILURE';

export const STATE_LANDING_PAGE_REQUEST =
	'SSDC/PAGES/STATE_LANDING_PAGE_REQUEST';
export const STATE_LANDING_PAGE_SUCCESS =
	'SSDC/PAGES/STATE_LANDING_PAGE_SUCCESS';
export const STATE_LANDING_PAGE_FAILURE =
	'SSDC/PAGES/STATE_LANDING_PAGE_FAILURE';

export const SUBMIT_CONTACT_FORM_REQUEST =
	'SSDC/PAGES/SUBMIT_CONTACT_FORM_REQUEST';
export const SUBMIT_CONTACT_FORM_SUCCESS =
	'SSDC/PAGES/SUBMIT_CONTACT_FORM_SUCCESS';
export const SUBMIT_CONTACT_FORM_FAILURE =
	'SSDC/PAGES/SUBMIT_CONTACT_FORM_FAILURE';

// ------------------------------------
// Initial State
// ------------------------------------
export const initialState = {
	home: {},
	stateContent: null,
	isLoadingState: false,
};

// ------------------------------------
// Reducer
// ------------------------------------

export default function reducer(state = initialState, action = {}) {
	switch (action.type) {
		case HOMEPAGE_SUCCESS:
			return {
				...state,
				home: {
					meta: action.payload.meta,
					featuredCities: action.payload.featuredCities,
					geolocatedFacilities:
						action.payload.geolocatedFacilities.nearbyFacilities,
				},
				content: {
					nearbyFacilities: {
						facilityCollection: {
							collection:
								action.payload.geolocatedFacilities
									.nearbyFacilities,
						},
					},
					nearbyCities:
						action.payload.geolocatedFacilities.nearbyCities,
				},
			};
		// Clear stale state content when starting to fetch new state page
		// This prevents flicker where old state data shows briefly during navigation
		case STATE_LANDING_PAGE_REQUEST:
			return {
				...state,
				isLoadingState: true,
				// Clear stateContent to prevent showing stale data during transition
				stateContent: null,
			};
		case STATE_LANDING_PAGE_SUCCESS:
			return {
				...state,
				isLoadingState: false,
				stateContent: {
					featuredCities: action.payload.featuredCities,
					geolocatedFacilities:
						action.payload?.geolocatedFacilities?.nearbyFacilities,
					allCities: action.payload.allCities,
					title: action.payload.title,
					body: action.payload.body,
					location: action.payload.location,
					contentId: action.payload.contentId,
					stateUrl: action.payload.stateUrl,
					meta: action.payload.meta,
					drilldownLinks: action.payload.drilldownLinks,
				},
			};
		case STATE_LANDING_PAGE_FAILURE:
			return {
				...state,
				isLoadingState: false,
				stateContent: {
					statusCode: 404,
				},
			};
		case PAID_LANDING_TEMPLATE_SUCCESS:
			return {
				...state,
				storageType: action.meta?.storageType,
				meta: action.meta || {
					title: `Find ${action.meta?.storageType} Storage Units Near You | SelfStorage`, // Need to confirm with paid marketing
					description: '', // Need to confirm with paid marketing
				},
				content: {
					nearbyFacilities: {
						facilityCollection: {
							collection:
								action.payload?.geolocatedFacilities
									?.nearbyFacilities || [],
						},
					},
				},
			};
		// Local workaround for API/SSL error to ensure we can dynamically
		// pass storageType to the PaidLandingPage component still
		case PAID_LANDING_TEMPLATE_FAILURE:
			return {
				storageType: action?.meta?.storageType || PPC_TYPE_STORAGE,
			};
		default:
			return state;
	}
}

// ------------------------------------
// Actions
// ------------------------------------
export function unsubscribeEmail(data) {
	return {
		[API]: {
			types: [
				UNSUBSCRIBE_REQUEST,
				UNSUBSCRIBE_SUCCESS,
				UNSUBSCRIBE_FAILURE,
			],
			method: 'POST',
			endpoint: '/api/unsubscribe',
			data,
		},
	};
}

export function getHomepage() {
	return {
		[API]: {
			types: [HOMEPAGE_REQUEST, HOMEPAGE_SUCCESS, HOMEPAGE_FAILURE],
			endpoint: '/api/page/index',
		},
	};
}

export function getDefaultPage() {
	return {
		[API]: {
			types: [
				DEFAULT_PAGE_REQUEST,
				DEFAULT_PAGE_SUCCESS,
				DEFAULT_PAGE_FAILURE,
			],
			endpoint: '/api/page/default',
		},
	};
}

export function getStateLandingPage(state) {
	return {
		[API]: {
			types: [
				STATE_LANDING_PAGE_REQUEST,
				STATE_LANDING_PAGE_SUCCESS,
				STATE_LANDING_PAGE_FAILURE,
			],
			endpoint: `/api/content/${state}`,
		},
	};
}

export function submitContactForm(data) {
	return {
		[API]: {
			types: [
				SUBMIT_CONTACT_FORM_REQUEST,
				SUBMIT_CONTACT_FORM_SUCCESS,
				SUBMIT_CONTACT_FORM_FAILURE,
			],
			method: 'POST',
			endpoint: '/api/contact',
			data,
		},
	};
}

export function getPaidLandingPage(queryParams) {
	const { utm_term, mkwid, utm_source } = queryParams;

	let storageType = PPC_TYPE_STORAGE;
	let keyWordVar = utm_term || mkwid;

	if (utm_source?.toLowerCase() === 'google' && keyWordVar) {
		// Decode and lowercase the keyWordVar
		const decodedTerm = decodeURIComponent(keyWordVar).toLowerCase();

		// Find if any PaidLandingTypes key is present in the keyWordVar
		const foundType = Object.keys(PaidLandingTypes).find((typeKey) =>
			decodedTerm.includes(typeKey),
		);

		if (foundType) {
			storageType = PaidLandingTypes[foundType];
		}
	}
	// For non-google sources, default to PPC_TYPE_STORAGE
	return {
		[API]: {
			types: [
				PAID_LANDING_TEMPLATE_REQUEST,
				PAID_LANDING_TEMPLATE_SUCCESS,
				PAID_LANDING_TEMPLATE_FAILURE,
			],
			meta: { storageType },
			endpoint: '/api/page/default',
		},
	};
}
